import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../utils/structuredData';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = ['all', 'Mindset', 'Budgeting', 'Basics', 'Side Hustles', 'Investing', 'Digital Products'];

  useEffect(() => {
    fetchPosts();
  }, [language, selectedCategory]);

  const fetchPosts = async () => {
    try {
      let url = `${API}/blog?language=${language}`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const seoTitle = language === 'ro'
    ? 'Blog - FinanciallyFree.ro | Articole fără bullshit despre bani'
    : 'Blog - FinanciallyFree.ro | Financial Freedom Articles';
    
  const seoDescription = language === 'ro'
    ? 'Articole practice despre cum să-ți iei banii în mână. Zero teorie inutilă, doar strategii testate. Side hustle-uri, investiții și FIRE pe înțelesul tuturor.'
    : 'Articles, guides, and strategies on saving, investing, side hustles, and FIRE. Practical financial education.';

  const baseUrl = window.location.origin;
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: baseUrl },
    { name: 'Blog', url: `${baseUrl}/blog` }
  ]);

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords="blog financiar, educație financiară, ghiduri investiții, FIRE România, side hustle"
        url="/blog"
        structuredData={breadcrumbSchema}
      />
      <div className="page-container min-h-screen py-16 bg-gradient-to-br from-mint-50 via-white to-blue-50">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-slate-900 sm:text-5xl" data-testid="blog-title">
            {t('blog.title')}
          </h1>
          <p className="text-lg text-slate-600">{t('blog.subtitle')}</p>
        </div>

        {/* Categories */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          <Badge
            className={`cursor-pointer px-4 py-2 text-sm ${
              selectedCategory === 'all'
                ? 'bg-teal-600'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
            onClick={() => setSelectedCategory('all')}
            data-testid="category-all"
          >
            {t('blog.all')}
          </Badge>
          {categories.slice(1).map((cat) => (
            <Badge
              key={cat}
              className={`cursor-pointer px-4 py-2 text-sm ${
                selectedCategory === cat
                  ? 'bg-teal-600'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
            >
              {cat}
            </Badge>
          ))}
        </div>

        {/* Posts Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <Card
              key={post.id}
              className="group cursor-pointer overflow-hidden border-0 shadow-md transition-all hover:shadow-2xl"
              onClick={() => navigate(`/blog/${post.slug}`)}
              data-testid={`blog-post-${post.slug}`}
            >
              {post.image_url && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
              )}
              <CardContent className="p-6">
                <Badge className="mb-3 bg-teal-600">{post.category}</Badge>
                <h2 className="mb-2 text-xl font-bold text-slate-900 group-hover:text-teal-600">
                  {post.title}
                </h2>
                <p className="mb-4 text-slate-600">{post.subtitle}</p>
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>{post.author}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

export default Blog;